import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconArrowsLeftRightTrailOutline18 as ArrowsLeftRight,
	IconPen2Outline18 as Edit2,
	IconEnvelopeMinusOutline18,
	IconEnvelopeOutline18,
	IconEnvelopeBanOutline18 as MailX,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import { normalizeEmail } from "@productlane/api/src/lib/contact"
import { generateId } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { isCustomContactEmail } from "@productlane/lib/src/custom-channel"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	AttioIcon,
	Avatar,
	Badge,
	Button,
	CopyButton,
	Empty,
	HubspotIcon,
	IconButton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { LinkedCompany } from "./contact-companies"
import EmptyCompany from "@/assets/empty-states/empty-company.png"
import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import {
	DetailScreenEditor,
	DetailScreenLayout,
} from "@/components/detail-screen"
import { DetailScreenWithConnectors } from "@/components/detail-screen/custom-connectors-sidebar"
import {
	DetailThreadsList,
	hasActiveDetailFilters,
	useDetailThreadsControls,
} from "@/components/detail-screen/detail-threads-list"
import { DetailThreadsToolbar } from "@/components/detail-screen/detail-threads-toolbar"
import { DetailUpvotesList } from "@/components/detail-screen/detail-upvotes-list"
import { useDetailListState } from "@/components/detail-screen/use-detail-list-state"
import { GroupedIssuesProjectsTable } from "@/components/issue-or-project-items/grouped-issues-projects-table"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useBackNavigation, useSetBackNavigation } from "@/lib/back-navigation"
import { getBlockedSenderId, isEmailBlocked } from "@/lib/email-utils"
import { useCategorizedIssues } from "@/lib/issues"
import { useCategorizedProjects } from "@/lib/projects"
import { areQueriesSettled, isQuerySettled } from "@/lib/query-settled"
import { useSession } from "@/lib/session"
import { useCmdkState } from "@/lib/states/cmdk-state"
import { useFilteredIssues, useFilteredProjects } from "@/lib/use-team-filter"
import { useRole } from "@/lib/useRole"
import { useTrial } from "@/lib/useTrial"
import { mutateOk, useZero } from "@/lib/zero"
import { HowItWorksButton } from "@/screens/settings/settings-switches/how-it-works-button"
import { CreateNewThreadButton } from "../inbox/create-new-thread-button"
import { DetailBreadcrumb, DetailTopBar } from "../page-header"
import { ContactSuppressionWarning } from "../threads/components/right-bar/contact-suppression-warning"
import { ContactCompanies } from "./contact-companies"
import { MergeContactDialog } from "./merge-contact-dialog"
import { neighboursOf, usePeopleOrder } from "./people-order"
import { UpdateContactDialog } from "./update-contact-dialog"

export const ContactsDetailScreen = () => {
	const zero = useZero()
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	const id = params.id!
	useSetBackNavigation({
		type: "contacts-detail",
		url: `/contacts/${id}`,
	})

	const [contact, contactResult] = useQuery(queries.contactDetails(id))
	const { data: workspaceInfo } = api.workspace.info.useQuery()
	const [projectsRaw, projectsResult] = useQuery(queries.projectsForContact(id))
	const [issuesRaw, issuesResult] = useQuery(queries.issuesForContact(id))
	const [blockedEmails] = useQuery(queries.blockedSenders())
	const [teamMembers] = useQuery(queries.teamMembers())

	const { session } = useSession()
	const { hasTrialEnded } = useTrial()

	// Filter by selected teams
	const projects = useFilteredProjects(projectsRaw)
	const issues = useFilteredIssues(issuesRaw)

	const [isConfirmDeleteOpen, setConfirmDelete] = useToggle(false)
	const [isUpdateContactOpen, setIsUpdateContactOpen] = useToggle(false)
	const [isMergeOpen, setIsMergeOpen] = useToggle(false)
	const { mergeContactWithId, clearMergeContactDialog } = useCmdkState()
	useEffect(() => {
		if (mergeContactWithId) setIsMergeOpen(true)
	}, [mergeContactWithId, setIsMergeOpen])
	const threadsState = useDetailListState()
	const upvotesState = useDetailListState()
	const [tab, setTab] = useState<"threads" | "upvotes">("threads")
	const threadControls = useDetailThreadsControls()

	const categorizedIssues = useCategorizedIssues(issues)
	const categorizedProjects = useCategorizedProjects(projects)

	const { goBack } = useBackNavigation()
	const close = useCallback(
		(shortcut = false) => {
			if (contact) {
				goBack("/contacts")
				analytics.track("contact-close", { id: contact.id, shortcut })
			}
		},
		[contact, goBack],
	)

	const isBlocked = useMemo(
		() => isEmailBlocked(contact?.email, blockedEmails),
		[contact?.email, blockedEmails],
	)

	const blockedSenderId = useMemo(
		() => getBlockedSenderId(contact?.email, blockedEmails),
		[contact?.email, blockedEmails],
	)

	const handleBlockSender = async () => {
		if (!contact) return

		if (isBlocked && blockedSenderId) {
			const result = await zero.mutate(
				mutators.blockedSenders.delete({
					id: blockedSenderId,
				}),
			).client

			if (result.type === "success") {
				toast.success({
					title: "Sender unblocked",
					description: `Emails from ${contact.email} will no longer be ignored.`,
					action: {
						label: "View Blocked Senders",
						callback: () => {
							void navigate({ to: "/settings/emails/blocklist" })
						},
					},
				})
				analytics.track("unblock-email-sender", {
					origin: "contact-details-page",
					email: contact.email,
				})
			} else {
				toast.error({
					title: "Failed to unblock sender",
					description:
						result.error instanceof Error
							? result.error.message
							: "Unknown error",
				})
			}
		} else {
			const result = await zero.mutate(
				mutators.blockedSenders.create({
					id: generateId(),
					value: normalizeEmail(contact.email),
					type: "EMAIL",
				}),
			).client

			if (result.type === "success") {
				toast.success({
					title: "Sender blocked",
					description: `Feedbacks from ${contact.email} will be automatically ignored.`,
					action: {
						label: "View Blocked Senders",
						callback: () => {
							void navigate({ to: "/settings/emails/blocklist" })
						},
					},
				})
				analytics.track("block-email-sender", {
					origin: "contact-details-page",
					email: contact.email,
				})
			} else {
				toast.error({
					title: "Failed to block sender",
					description:
						result.error instanceof Error
							? result.error.message
							: "Unknown error",
				})
			}
		}
	}

	const handleToggleSubscribed = useCallback(async () => {
		if (!contact) return

		const result = await zero.mutate(
			mutators.contacts.update({
				id: contact.id,
				isSubscribed: !contact.isSubscribed,
			}),
		).client

		if (result.type === "success") {
			toast.success({
				title: contact.isSubscribed ? "Unsubscribed" : "Subscribed",
				description: contact.isSubscribed
					? "This contact will no longer receive email updates."
					: "This contact will receive email updates.",
			})
			analytics.track("toggle-contact-subscribed", {
				origin: "contact-details-page",
				id: contact.id,
				isSubscribed: !contact.isSubscribed,
			})
		} else {
			toast.error({
				title: "Failed to update subscription",
				description:
					result.error instanceof Error
						? result.error.message
						: "Unknown error",
			})
		}
	}, [contact, zero])

	// The primary lives on Contact.companyId and may have no junction row yet, so
	// it is folded in rather than read from the links alone.
	const linkedCompanies = useMemo(() => {
		const byId = new Map<string, LinkedCompany>()
		if (contact?.company) byId.set(contact.company.id, contact.company)
		for (const link of contact?.companyLinks ?? []) {
			if (link.company) byId.set(link.company.id, link.company)
		}
		return [...byId.values()]
	}, [contact?.company, contact?.companyLinks])

	const handleChangeCompany = useCallback(
		async (companyId: string | null) => {
			if (!contact) return

			const result = await zero.mutate(
				mutators.contacts.update({
					id: contact.id,
					companyId,
				}),
			).client

			if (result.type === "success") {
				toast.success({
					title: companyId ? "Customer updated" : "Customer removed",
				})
				analytics.track("change-contact-company", {
					origin: "contact-details-page",
					id: contact.id,
					companyId,
				})
			} else {
				toast.error({
					title: "Failed to update customer",
					description:
						result.error instanceof Error
							? result.error.message
							: "Unknown error",
				})
			}
		},
		[contact, zero],
	)

	useShortcut(
		"esc",
		() => {
			close(true)
		},
		{ enabled: !isConfirmDeleteOpen },
	)
	const peopleOrder = usePeopleOrder((state) => state.ids)
	const { nextId, previousId } = neighboursOf(peopleOrder, id)
	useShortcut(
		"j",
		() => {
			if (nextId)
				void navigate({
					to: "/people/$id",
					params: { id: nextId },
					replace: true,
				})
		},
		{ enabled: !!nextId && !isConfirmDeleteOpen },
	)
	useShortcut(
		"k",
		() => {
			if (previousId)
				void navigate({
					to: "/people/$id",
					params: { id: previousId },
					replace: true,
				})
		},
		{ enabled: !!previousId && !isConfirmDeleteOpen },
	)
	const { role } = useRole()
	useShortcut(
		"meta+backspace, ctrl+backspace",
		() => {
			analytics.track("open-delete-contact-dialog", {
				id: contact?.id,
				shortcut: true,
			})
			setConfirmDelete(true)
		},
		{ enabled: role !== "VIEWER" },
	)
	useShortcut(
		"e",
		() => {
			if (contact) {
				setIsUpdateContactOpen(true)
				analytics.track("open-update-contact-dialog", {
					id: contact.id,
					shortcut: true,
				})
			}
		},
		{ preventDefault: true, enabled: role !== "VIEWER" },
	)

	const isEmptyState =
		areQueriesSettled(projectsResult, issuesResult) &&
		threadsState.isSettled &&
		Object.values(categorizedProjects).every((arr) => arr.length === 0) &&
		Object.values(categorizedIssues).every((arr) => arr.length === 0) &&
		threadsState.size === 0

	if (!contact) {
		if (!isQuerySettled(contactResult)) return null
		return <EmptyContactDetailsScreen />
	}

	return (
		<>
			<MetaInfo title="Contact Detail" />
			<AlertDialog
				isOpen={isConfirmDeleteOpen}
				onOpenChange={setConfirmDelete}
				confirmText="Delete"
				title="Are you sure?"
				description="Do you really want to delete this contact?"
				onCancel={() => {
					setConfirmDelete(false)
				}}
				onConfirm={async () => {
					void navigate({ to: "/contacts", replace: true })
					await mutateOk(zero.mutate(mutators.contacts.delete({ id })))
				}}
			/>
			<MergeContactDialog
				contactId={contact.id}
				isOpen={isMergeOpen}
				onOpenChange={(open) => {
					setIsMergeOpen(open)
					if (!open) clearMergeContactDialog()
				}}
				initialWithId={mergeContactWithId}
			/>
			<UpdateContactDialog
				isOpen={isUpdateContactOpen}
				onOpenChange={setIsUpdateContactOpen}
				values={{
					id: contact.id,
					email: contact.email,
					name: contact.name ?? "",
				}}
			/>
			<DetailScreenWithConnectors target={{ type: "contact", id: contact.id }}>
				<DetailTopBar className="justify-between" clearsNavToggle>
					<div className="flex min-w-0 items-center gap-1">
						<DetailBreadcrumb
							parentLabel="People"
							onParentClick={() => close()}
							currentIcon={
								<Avatar
									size="xs"
									name={contact.name || contact.email}
									image={contact.imageUrl}
								/>
							}
							currentLabel={contact.name || contact.email}
						/>
						<RoleCheck not={["VIEWER"]}>
							<DropdownMenu
								trigger={
									<IconButton
										Icon={MoreVertical}
										variant="ghost"
										iconClassName="fill-current"
									/>
								}
								contentProps={{ side: "bottom", align: "start" }}
							>
								<DropdownMenuGroup>
									<DropdownMenuItem
										Icon={Edit2}
										shortcut="E"
										onClick={() => {
											setIsUpdateContactOpen(true)
											analytics.track("open-update-contact-dialog", {
												id: contact.id,
												shortcut: false,
											})
										}}
									>
										Edit
									</DropdownMenuItem>
									{contact.attioId && workspaceInfo?.attioWorkspaceSlug && (
										<DropdownMenuItem
											Icon={AttioIcon}
											onClick={() => {
												analytics.track("open-contact-in-attio", {
													id: contact.id,
												})
												window.open(
													`https://app.attio.com/${workspaceInfo.attioWorkspaceSlug}/person/${contact.attioId}`,
													"_blank",
												)
											}}
										>
											Open in Attio
										</DropdownMenuItem>
									)}
									{contact.hubspotId && workspaceInfo?.hubspotPortalId && (
										<DropdownMenuItem
											Icon={HubspotIcon}
											onClick={() => {
												analytics.track("open-contact-in-hubspot", {
													id: contact.id,
												})
												window.open(
													`https://app.hubspot.com/contacts/${workspaceInfo.hubspotPortalId}/record/0-1/${contact.hubspotId}`,
													"_blank",
												)
											}}
										>
											Open in HubSpot
										</DropdownMenuItem>
									)}
									<DropdownMenuItem
										Icon={ArrowsLeftRight}
										onClick={() => {
											setIsMergeOpen(true)
											analytics.track("open-merge-contact-dialog", {
												id: contact.id,
											})
										}}
									>
										Merge
									</DropdownMenuItem>
									<DropdownMenuItem
										Icon={MailX}
										onClick={async () => {
											await handleBlockSender()
										}}
									>
										{isBlocked ? "Unblock sender" : "Block sender"}
									</DropdownMenuItem>
									<DropdownMenuItem
										Icon={Trash}
										shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`}
										onClick={() => {
											setConfirmDelete(true)
											analytics.track("open-delete-contact-dialog", {
												id: contact.id,
												shortcut: false,
											})
										}}
									>
										Delete
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenu>
						</RoleCheck>
					</div>
					<div className="flex-1" />

					<div className="flex items-center gap-2">
						{contact.isEmailSuppressed &&
							!isCustomContactEmail(contact.email) && (
								<ContactSuppressionWarning
									contactId={contact.id}
									variant="badge"
								/>
							)}
						<Tooltip
							label={
								contact.isSubscribed
									? "Unsubscribe contact from all email updates."
									: "Subscribe contact to all your email updates."
							}
							enabled={session?.membershipRole !== "VIEWER"}
						>
							<Button
								variant="outline"
								size="sm"
								disabled={session?.membershipRole === "VIEWER"}
								onClick={() => {
									void handleToggleSubscribed()
								}}
								Icon={
									contact.isSubscribed
										? IconEnvelopeOutline18
										: IconEnvelopeMinusOutline18
								}
							>
								{contact.isSubscribed ? "Subscribed" : "Unsubscribed"}
							</Button>
						</Tooltip>

						<RoleCheck not={["VIEWER"]}>
							<CreateNewThreadButton
								disabled={hasTrialEnded}
								contactId={contact.id}
								companyId={contact.companyId ?? undefined}
								withLabel
							/>
						</RoleCheck>

						{isBlocked && (
							<Badge className="max-h-6 min-h-6" variant="red">
								Blocked
							</Badge>
						)}
					</div>
				</DetailTopBar>
				<DetailScreenLayout>
					<DetailScreenEditor isPadded={false}>
						{/* <div className=" mx-auto flex min-w-[800px] flex-col"> */}
						<div className="flex items-center justify-between gap-4 p-5 sm:px-6 sm:py-10">
							<div className="flex w-full items-center gap-4">
								<Avatar
									size="xl"
									name={contact.name || contact.email}
									image={contact.imageUrl}
									roundness="full"
								/>
								<div className="flex w-full items-start justify-between">
									<div className="flex flex-col">
										<div className="flex items-center gap-4">
											{contact.name && (
												<div className="truncate font-medium">
													{contact.name}
												</div>
											)}
											<div className="group flex items-center gap-1 text-secondary">
												<button
													type="button"
													className="truncate text-left hover:text-primary"
													onClick={async () => {
														await navigator.clipboard.writeText(contact.email)
														toast.success({
															title: "Email copied to clipboard",
														})
													}}
												>
													{contact.email}
												</button>
												<CopyButton
													value={contact.email}
													className="opacity-0 transition-opacity duration-100 group-hover:opacity-100"
												/>
											</div>
										</div>

										<div className="-ml-2">
											<ContactCompanies
												contactId={contact.id}
												primaryCompanyId={contact.companyId ?? null}
												companies={linkedCompanies}
												onPromote={(companyId) => {
													void handleChangeCompany(companyId)
												}}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						<GroupedIssuesProjectsTable
							categorizedIssues={categorizedIssues}
							categorizedProjects={categorizedProjects}
						/>
						{(Object.values(categorizedProjects).some(
							(arr) => arr.length > 0,
						) ||
							Object.values(categorizedIssues).some((arr) => arr.length > 0)) &&
							threadsState.size > 0 && (
								<div className="flex w-full items-center justify-center px-6 sm:my-6">
									<div className="w-full border-t" />
								</div>
							)}
						<Tabs
							value={tab}
							onValueChange={(value) => {
								if (value === "threads" || value === "upvotes") {
									setTab(value)
								}
							}}
							className={cn({
								hidden: isEmptyState,
							})}
						>
							<div className="my-4 flex min-h-7 w-full flex-row items-center justify-between px-5 sm:px-8">
								<TabsList className="w-fit justify-start">
									<TabsTrigger value="threads">Threads</TabsTrigger>
									<TabsTrigger value="upvotes">Upvotes</TabsTrigger>
								</TabsList>
								{tab === "threads" &&
									(threadsState.size > 0 ||
										hasActiveDetailFilters(threadControls.filters)) && (
										<DetailThreadsToolbar
											sortOrder={threadControls.sortOrder}
											onSortOrderChange={threadControls.setSortOrder}
											filters={threadControls.filters}
											onFiltersChange={threadControls.setFilters}
											teamMembers={teamMembers}
										/>
									)}
							</div>

							<TabsContent value="threads">
								<DetailThreadsList
									contactId={id}
									hideLabel
									controls={threadControls}
									setThreadsSize={threadsState.report}
								/>

								{!isEmptyState && threadsState.isEmpty && (
									<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
										<Empty
											text="No customer requests yet. Link issues or projects to this contact in the thread, or sync them from Linear."
											title="Customer requests"
											className="max-w-[20rem]"
											variant="left"
											image={
												<img src={EmptyIssue} alt="Empty customer requests" />
											}
											action={
												<div className="flex items-center gap-4">
													<Button
														onClick={() => {
															void navigate({ to: "/threads" })
														}}
														className="min-w-32"
													>
														Go to Inbox
													</Button>
													<HowItWorksButton
														className="min-h-7"
														article="7e861235-26a4-4b00-9268-a0e82258bbe6"
													/>
												</div>
											}
										/>
									</div>
								)}
							</TabsContent>
							<TabsContent value="upvotes">
								<DetailUpvotesList
									contactId={id}
									hideLabel
									setThreadsSize={upvotesState.report}
								/>

								{!isEmptyState && upvotesState.isEmpty && (
									<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
										<Empty
											text="This contact did not upvote any issue or project on your feedback portal."
											title="Upvote"
											className="max-w-[20rem]"
											variant="left"
											image={<img src={EmptyIssue} alt="Empty upvotes" />}
											action={
												<div className="flex items-center gap-4">
													<Button
														onClick={() => {
															void navigate({ to: "/edit-requests" })
														}}
														className="min-w-32"
													>
														Go to Requests
													</Button>
													<HowItWorksButton
														className="min-h-7"
														article="bb5ca077-16c1-4d97-811e-c3107d2f1e87"
													/>
												</div>
											}
										/>
									</div>
								)}
							</TabsContent>
						</Tabs>

						{isEmptyState && (
							<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
								<Empty
									text="No customer requests yet. Link issues or projects to this contact in the thread, or sync them from Linear."
									title="Customer requests"
									className="max-w-[20rem]"
									variant="left"
									image={<img src={EmptyIssue} alt="Empty customer requests" />}
									action={
										<div className="flex items-center gap-4">
											<Button
												onClick={() => {
													void navigate({ to: "/threads" })
												}}
												className="min-w-32"
											>
												Go to Inbox
											</Button>
											<HowItWorksButton
												className="min-h-7"
												article="7e861235-26a4-4b00-9268-a0e82258bbe6"
											/>
										</div>
									}
								/>
							</div>
						)}
					</DetailScreenEditor>
				</DetailScreenLayout>
			</DetailScreenWithConnectors>
		</>
	)
}

const EmptyContactDetailsScreen = () => {
	return (
		<div className="flex min-h-0 w-full flex-1 items-center justify-center">
			<Empty
				text="It may have been moved, deleted, or you may not have access to it."
				title="Contact not found"
				className="max-w-[20rem]"
				variant="center"
				image={<img src={EmptyCompany} alt="Contact not found" />}
			/>
		</div>
	)
}
